/**
 * Biblioteca (SDD §7.2): grabaciones agrupadas por día, filtros, búsqueda en
 * transcripciones y polling mientras haya trabajo en curso (SDD §7.3).
 * También sirve la vista de proyecto (`/p/:id`).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useOutletContext, useParams } from 'react-router'
import { ACTIVE_STATUSES, type Recording, type TopicTracker } from '@eco/shared'
import { useApp, useToast, errorText } from '../components/AppContext'
import { AskAllPanel } from '../components/AskAllPanel'
import { ErrorBox, Skeleton, StatusBadge } from '../components/ui'
import { IconSearch } from '../components/icons'
import { formatClock, formatDayGroup, formatLength } from '../lib/format'
import { splitHighlight } from '../lib/highlight'
import { useAsync, useDebounced, useDocumentTitle, usePolling } from '../lib/hooks'

type Filtro = 'todos' | 'activo' | 'retenida'

export function Library() {
  const { id: projectId } = useParams()
  const { api, refreshUsage, org, me } = useApp()
  const enEquipo = org?.tipo === 'equipo'
  const manageFolders = !enEquipo || org?.rol === 'owner' || org?.rol === 'admin'
  const toast = useToast()
  const nav = useNavigate()
  const outlet = useOutletContext<{ reloadProjects?: () => Promise<void> } | undefined>()
  const [filtro, setFiltro] = useState<Filtro>('todos')
  const [q, setQ] = useState('')
  const query = useDebounced(q.trim(), 300)
  // Páginas cargadas con «Ver más» y su cursor; null = seguir el de la primera página
  const [extra, setExtra] = useState<Recording[]>([])
  const [moreCursor, setMoreCursor] = useState<string | null | undefined>(undefined)
  const [loadingMore, setLoadingMore] = useState(false)

  const project = useAsync(
    () =>
      projectId
        ? api.projects().then((ps) => ps.find((p) => p.id === projectId) ?? null)
        : Promise.resolve(null),
    [api, projectId]
  )
  useDocumentTitle(projectId ? project.data?.nombre : 'Biblioteca')

  // La primera página se recarga con el polling; las siguientes se conservan
  const list = useAsync(
    () => api.listRecordings({ projectId, estado: filtro, limit: 50 }),
    [api, projectId, filtro]
  )
  useEffect(() => {
    setExtra([])
    setMoreCursor(undefined)
  }, [projectId, filtro])
  const cursor = moreCursor === undefined ? (list.data?.nextCursor ?? null) : moreCursor

  const [dias, setDias] = useState<number | undefined>(undefined)
  const search = useAsync(
    () => (query.length >= 2 ? api.search(query, projectId, dias) : Promise.resolve(null)),
    [api, query, projectId, dias]
  )
  // «/» enfoca la búsqueda (si no se está escribiendo en otro campo)
  const searchRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  const trackers = useAsync(() => api.trackers().catch(() => [] as TopicTracker[]), [api])

  const items = useMemo(() => {
    const first = list.data?.items ?? []
    const seen = new Set(first.map((r) => r.id))
    return [...first, ...extra.filter((r) => !seen.has(r.id))]
  }, [list.data, extra])
  const working = items.some((r) => (ACTIVE_STATUSES as readonly string[]).includes(r.estado))
  usePolling(
    () => {
      void list.reload()
      void refreshUsage()
    },
    10_000,
    working && !loadingMore
  )

  async function more() {
    if (!cursor) return
    setLoadingMore(true)
    try {
      const page = await api.listRecordings({ projectId, estado: filtro, limit: 50, cursor })
      setExtra((e) => [...e, ...page.items])
      setMoreCursor(page.nextCursor)
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setLoadingMore(false)
    }
  }

  async function renameProject() {
    const p = project.data
    if (!p) return
    const nombre = prompt('Nombre del proyecto', p.nombre)?.trim()
    if (!nombre || nombre === p.nombre) return
    try {
      await api.patchProject(p.id, { nombre })
      await project.reload()
      await outlet?.reloadProjects?.()
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  async function toggleShared() {
    const p = project.data
    if (!p) return
    try {
      await api.patchProject(p.id, { compartido: !p.compartido })
      await project.reload()
      toast(
        p.compartido
          ? 'La carpeta ya no es del equipo: cada reunión vuelve a ser de quien la grabó'
          : 'Carpeta del equipo: todos ven sus reuniones'
      )
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  async function deleteProject() {
    const p = project.data
    if (
      !p ||
      !confirm(
        `¿Eliminar el proyecto «${p.nombre}»? Las grabaciones no se borran: quedan sin proyecto.`
      )
    )
      return
    try {
      await api.deleteProject(p.id)
      await outlet?.reloadProjects?.()
      nav('/')
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  const groups = useMemo(() => {
    const g: { day: string; items: Recording[] }[] = []
    for (const r of items) {
      const day = formatDayGroup(r.fechaInicio)
      const last = g[g.length - 1]
      if (last?.day === day) last.items.push(r)
      else g.push({ day, items: [r] })
    }
    return g
  }, [items])

  return (
    <div className="page">
      <div className="page-head">
        <div className="grow">
          <h1>{projectId ? (project.data?.nombre ?? 'Proyecto') : 'Biblioteca'}</h1>
          {project.data?.descripcion && <p className="muted">{project.data.descripcion}</p>}
        </div>
        {projectId && project.data && manageFolders && (
          <div className="row">
            {enEquipo && (
              <button
                className={`chip ${project.data.compartido ? 'on' : ''}`}
                aria-pressed={project.data.compartido}
                onClick={() => void toggleShared()}
                title="En una carpeta del equipo, todos los miembros ven sus reuniones"
              >
                {project.data.compartido ? 'Carpeta del equipo' : 'Compartir carpeta con el equipo'}
              </button>
            )}
            <button className="btn sm ghost" onClick={() => void renameProject()}>
              Renombrar
            </button>
            <button className="btn sm ghost danger" onClick={() => void deleteProject()}>
              Eliminar proyecto
            </button>
          </div>
        )}
      </div>

      {items.some((r) => r.estado === 'completed') && (
        <AskAllPanel key={projectId ?? 'todas'} projectId={projectId} />
      )}

      <div className="row wrap" style={{ marginBottom: 8 }}>
        <label className="search grow" style={{ minWidth: 220 }}>
          <IconSearch />
          <span className="sr">Buscar en tus reuniones</span>
          <input
            ref={searchRef}
            type="search"
            aria-keyshortcuts="/"
            placeholder="Buscar en lo que se dijo en tus reuniones…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            maxLength={200}
          />
        </label>
        {query && (
          <select
            aria-label="Fecha de las reuniones"
            value={dias ?? ''}
            onChange={(e) => setDias(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">Cualquier fecha</option>
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
        )}
        {!query && (
          <div className="filters" role="group" aria-label="Filtrar">
            {(
              [
                ['todos', 'Todas'],
                ['activo', 'En proceso'],
                ['retenida', 'Retenidas']
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                className={`chip ${filtro === k ? 'on' : ''}`}
                aria-pressed={filtro === k}
                onClick={() => setFiltro(k)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {(trackers.data?.length ?? 0) > 0 && (
        <div className="row wrap" style={{ gap: 6, marginBottom: 8 }}>
          <span className="faint" style={{ fontSize: 12 }}>
            Temas
          </span>
          {trackers.data!.map((t) => {
            const consulta = t.terminos.join(' or ')
            return (
              <button
                key={t.id}
                className={`chip ${q === consulta ? 'on' : ''}`}
                aria-pressed={q === consulta}
                onClick={() => setQ(q === consulta ? '' : consulta)}
                title={`Reuniones que mencionan: ${t.terminos.join(', ')}`}
              >
                {t.nombre}
              </button>
            )
          })}
        </div>
      )}

      {query.length >= 2 ? (
        <SearchResults
          loading={search.loading}
          error={search.error}
          hits={search.data ?? []}
          q={query}
        />
      ) : list.error ? (
        <ErrorBox error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading && !list.data ? (
        <div className="stack" style={{ marginTop: 18 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} h={52} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyLibrary filtered={filtro !== 'todos' || Boolean(projectId)} />
      ) : (
        <>
          {groups.map((g) => (
            <section key={g.day} aria-label={g.day}>
              <div className="group-title">{g.day}</div>
              <div className="rec-list">
                {g.items.map((r) => (
                  <RecordingRow key={r.id} r={r} marcar={enEquipo} yo={me?.user.id} />
                ))}
              </div>
            </section>
          ))}
          {cursor && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button className="btn" onClick={() => void more()} disabled={loadingMore}>
                {loadingMore ? 'Cargando…' : 'Ver más'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function RecordingRow({ r, marcar = false, yo }: { r: Recording; marcar?: boolean; yo?: string }) {
  return (
    <Link to={`/r/${r.id}`} className="rec-item">
      <div style={{ minWidth: 0 }}>
        <div className="t">{r.titulo}</div>
        <div className="m">
          {formatClock(r.fechaInicio)}
          {/* En un equipo: de quién es y si la ve todo el equipo */}
          {marcar &&
            (r.ownerUserId !== yo
              ? ' · de otra persona'
              : r.visibilidad === 'equipo'
                ? ' · compartida'
                : ' · privada')}
          {r.origen === 'import'
            ? ' · importada'
            : r.modo === 'presencial'
              ? ' · presencial'
              : r.plataforma === 'meet'
                ? ' · Meet'
                : ''}
        </div>
      </div>
      <span className="dur">{r.duracionMs ? formatLength(r.duracionMs) : ''}</span>
      <StatusBadge estado={r.estado} />
    </Link>
  )
}

function SearchResults({
  loading,
  error,
  hits,
  q
}: {
  loading: boolean
  error: Error | null
  hits: { recording: { id: string; titulo: string; fechaInicio: string }; fragmentos: string[] }[]
  q: string
}) {
  if (error) return <ErrorBox error={error} />
  if (loading && !hits.length) return <Skeleton h={80} />
  if (!hits.length)
    return (
      <div className="empty">
        <h2>Sin resultados para «{q}»</h2>
        <p className="muted">Busca palabras que se hayan dicho en la reunión o parte del título.</p>
      </div>
    )
  return (
    <div className="rec-list" style={{ marginTop: 12 }}>
      {hits.map((h) => (
        <Link
          key={h.recording.id}
          to={`/r/${h.recording.id}`}
          className="rec-item"
          style={{ gridTemplateColumns: '1fr' }}
        >
          <div style={{ minWidth: 0 }}>
            <div className="t">{h.recording.titulo}</div>
            <div className="m">{formatDayGroup(h.recording.fechaInicio)}</div>
            {h.fragmentos.slice(0, 3).map((f, i) => (
              <div key={i} className="hit muted" style={{ fontSize: 13, marginTop: 4 }}>
                …
                {splitHighlight(f).map((p, j) =>
                  p.hit ? <mark key={j}>{p.text}</mark> : <span key={j}>{p.text}</span>
                )}
                …
              </div>
            ))}
          </div>
        </Link>
      ))}
    </div>
  )
}

function EmptyLibrary({ filtered }: { filtered: boolean }) {
  if (filtered)
    return (
      <div className="empty">
        <h2>No hay grabaciones aquí</h2>
        <p className="muted">Cuando grabes o muevas reuniones a esta vista, aparecerán aquí.</p>
      </div>
    )
  return (
    <div className="empty">
      <h2>Tu primera reunión</h2>
      <p className="muted" style={{ maxWidth: 440, margin: '0 auto 16px' }}>
        Graba una prueba de 30 segundos para ver cómo quedan la transcripción y las notas, o sube un
        audio que ya tengas.
      </p>
      <div className="row" style={{ justifyContent: 'center' }}>
        <Link className="btn primary" to="/bienvenida">
          Graba una prueba
        </Link>
        <Link className="btn" to="/import">
          Importar audio
        </Link>
      </div>
    </div>
  )
}
